import { TicketTags } from '../components/TicketTags.js';
import { RichTextEditor } from '../components/RichTextEditor.js';
import { SuggestReplyButton, SuggestReplyResult, useSuggestReply } from '../components/SuggestReply.js';
import { MarkdownBody } from '../components/MarkdownBody.js';
import { TicketManage } from '../components/TicketManage.js';
import { useEffect, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  FolderOpen,
  Hash,
  Hourglass,
  Lock,
  MessageSquare,
  Plane,
  Route,
  ScrollText,
  Send,
  Timer,
  UserRound,
  Sparkles,
} from 'lucide-react';
import { canTransition, sameRequestType, type TicketPriority } from '@esa/shared';
import {
  api,
  type FieldDef,
  type Me,
  type TenantConfig,
  type TicketDetail,
  type TicketState,
} from '../lib/api.js';
import { ownerOptions } from '../lib/assignee.js';
import { awayLabel } from '../lib/away.js';
import { TicketSummary } from '../components/TicketSummary.js';
import {
  KnownSolutions,
  KnownSolutionsHint,
  KnownSolutionsTabIcon,
  useKnownSolutions,
} from '../components/KnownSolutions.js';
import { SystemActionsPanel, useSystemActions } from '../components/SystemActions.js';
import { ViewingNow } from '../components/ViewingNow.js';
import { TicketLinks } from '../components/TicketLinks.js';
import { canUseActions, hasCapability, hasRole } from '../lib/access.js';
import { NO_TYPE, requestTypeOptions } from '../lib/requestTypes.js';
import { RequesterHistory } from '../components/RequesterHistory.js';
import { ticketPageEscape } from '../lib/queueKeys.js';
import { SolvedBefore } from '../components/SolvedBefore.js';
import { TicketAsks } from '../components/TicketAsks.js';
import { TicketAccessLog } from '../components/TicketAccessLog.js';
import { TicketCc } from '../components/TicketCc.js';
import { AiProofButton, AiProofResult, useAiProof } from '../components/AiProof.js';
import { dateTime, hours, minutes, personLabel, SOURCE_LABEL } from '../lib/format.js';
import {
  Avatar,
  Badge,
  Button,
  Callout,
  ConfidenceBadge,
  ErrorState,
  Field,
  Input,
  Panel,
  Segmented,
  PRIORITY_OPTIONS,
  PriorityLabel,
  Select,
  Skeleton,
  SlaBadge,
  StateBadge,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
  Tooltip,
  EventBadge,
  VipBadge,
  cn,
  useToast,
  Menu,
  MenuContent,
  MenuItem,
  MenuTrigger,
} from '../ui/index.js';
import { STATE_LABEL } from '../lib/format.js';
import {
  AttachControl,
  AttachmentList,
  StagedFiles,
  type Attachment,
  type Staged,
} from '../components/Attachments.js';

const PENDING: TicketState[] = [
  'pending_requester',
  'pending_external',
  'pending_internal',
  'pending_event',
];

/** Local `YYYY-MM-DD`, not UTC — see the note in TicketActions. */
function dateInputValue(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

function endOfLocalDay(value: string): Date {
  const [y, m, d] = value.split('-').map(Number);
  return new Date(y!, (m ?? 1) - 1, d ?? 1, 23, 59, 0, 0);
}

export function TicketPage() {
  const { tenant = '', number = '' } = useParams();
  const qc = useQueryClient();
  const toast = useToast();
  const [reply, setReply] = useState('');
  const [replyKind, setReplyKind] = useState<'reply' | 'note'>('reply');
  /**
   * Files chosen for the message being written. Local until it is sent —
   * an attachment has to hang off a conversation entry, and that entry does
   * not exist until the reply is posted.
   */
  const [staged, setStaged] = useState<Staged[]>([]);
  const [tab, setTab] = useState('conversation');
  /**
   * Who else has this open. Held here rather than inside `ViewingNow` so the
   * header can decide whether to draw the separator before it — a dot with
   * nothing after it is worse than no dot.
   */
  const [viewers, setViewers] = useState<
    { oid: string; name: string | null; email: string | null; secondsAgo: number; arrived: boolean }[]
  >([]);

  const q = useQuery({
    queryKey: ['ticket', tenant, number],
    queryFn: () => api.get<TicketDetail>(`/api/v1/tenants/${tenant}/tickets/${number}`),
  });
  const cfg = useQuery({
    queryKey: ['config', tenant],
    queryFn: () => api.get<TenantConfig>(`/api/v1/tenants/${tenant}/config`),
  });
  // Shell already holds this, so it is a cache read rather than a request.
  const me = useQuery({ queryKey: ['me'], queryFn: () => api.get<Me>('/api/v1/me') });

  // Agents only: the internal lane is written for the desk, and a requester
  // must not be shown it (the API refuses as well).
  const isAgent = hasRole(me.data?.tenants.find((x) => x.id === tenant)?.role, 'agent');
  const solutions = useKnownSolutions(tenant, number, isAgent);

  /**
   * Actions are offered only to someone who could actually authorize one.
   *
   * Two conditions, both necessary: the group has the module, and this
   * person holds `action_operator`. The API enforces both independently —
   * this decides whether to render a tab that would otherwise 403 or 404 on
   * open, which reads as a broken page rather than as an absent feature.
   */
  const canAct = canUseActions(me.data, tenant, cfg.data?.modules ?? []);
  // Only where the move is legal: not on a resolved or closed ticket, and
  // not when it is already waiting on them.
  const canWaitOnRequester =
    replyKind === 'reply' && !!q.data && canTransition(q.data.ticket.state as never, 'pending_requester');
  const suggestOn = (cfg.data?.modules ?? []).includes('reply_suggestions') && hasCapability(me.data, tenant, 'ticket.reply');
  const systemActions = useSystemActions(tenant, number, canAct);
  const solutionCount = isAgent ? (solutions.data?.suggestions.length ?? 0) : 0;

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['ticket', tenant, number] });
    void qc.invalidateQueries({ queryKey: ['tickets'] });
  };

  /**
   * POST THE MESSAGE, THEN THE FILES, in that order and never the reverse.
   *
   * An attachment hangs off a conversation entry, so the entry has to exist
   * before a file can name it. Uploading first would leave files floating on
   * the ticket if the message then failed to post — visible to everyone,
   * belonging to nothing.
   *
   * A file that fails AFTER the message is sent is reported and not retried
   * silently: the reply has already gone to the requester, and the honest
   * thing is to say which file did not go with it rather than to imply the
   * whole thing failed.
   */
  const post = useMutation({
    mutationFn: async ({ then, ...body }: { kind: 'reply' | 'note'; body: string; then?: 'pending_requester' }) => {
      // HOW MANY FILES FOLLOW, so the reply's email waits for them rather
      // than leaving seconds before they exist. Released below once they are
      // up (or have failed); the server sends anyway after a few minutes.
      const entry = await api.post<{ id: string }>(
        `/api/v1/tenants/${tenant}/tickets/${number}/conversation`,
        {
          ...body,
          ...(staged.length > 0 ? { files: staged.length } : {}),
          ...(body.kind === 'reply' && fromSuggestion ? { suggestion: fromSuggestion } : {}),
        },
      );

      const failures: string[] = [];
      for (const item of staged) {
        setStaged((cur) =>
          cur.map((x) => (x.key === item.key ? { ...x, state: 'uploading' } : x)),
        );
        try {
          const form = new FormData();
          form.append('conversationId', entry.id);
          form.append('file', item.file, item.file.name);
          await api.upload(`/api/v1/tenants/${tenant}/tickets/${number}/attachments`, form);
          setStaged((cur) => cur.filter((x) => x.key !== item.key));
        } catch (err) {
          failures.push(item.file.name);
          const why = err instanceof Error ? err.message : 'upload failed';
          setStaged((cur) =>
            cur.map((x) => (x.key === item.key ? { ...x, state: 'failed', error: why } : x)),
          );
        }
      }
      if (body.kind === 'reply' && staged.length > 0) {
        // Send the email now, with whatever made it. Not fatal if it fails:
        // the hold expires and the email goes regardless.
        await api
          .post(`/api/v1/tenants/${tenant}/tickets/${number}/conversation/${entry.id}/release`, {})
          .catch(() => undefined);
      }
      // "Send and wait for the requester": the reply is the question, so the
      // clock stops on them from the moment it goes. A failure here does not
      // unsend the reply, so it is reported rather than thrown.
      let waitError: string | null = null;
      if (then === 'pending_requester') {
        await api
          .post(`/api/v1/tenants/${tenant}/tickets/${number}/state`, {
            state: 'pending_requester',
            reason: 'Waiting for the requester to reply',
          })
          .catch((e: unknown) => {
            waitError = e instanceof Error ? e.message : 'the status did not change';
          });
      }
      return { kind: body.kind, failures, waiting: then === 'pending_requester' && !waitError, waitError };
    },
    onSuccess: ({ kind, failures, waiting, waitError }) => {
      setReply('');
      if (kind === 'reply') setFromSuggestion(null);
      invalidate();
      if (waitError) {
        toast.error('Reply sent, but the status did not change', waitError);
        return;
      }
      if (failures.length > 0) {
        toast.error(
          kind === 'reply' ? 'Reply sent, but not everything attached' : 'Note added, but not everything attached',
          `${failures.join(', ')} did not upload. The message has already gone.`,
        );
        return;
      }
      toast.success(
        kind === 'reply' ? 'Reply sent' : 'Note added',
        kind === 'reply'
          ? waiting
            ? 'The requester has been emailed, and the ticket is waiting on them.'
            : 'The requester has been emailed.'
          : 'Internal only.',
      );
    },
    onError: (e) => toast.error('Could not post', e instanceof Error ? e.message : undefined),
  });

  /** Files already stored, grouped by the message they belong to. */
  const attachments: Attachment[] = (q.data?.attachments ?? []) as Attachment[];
  const filesFor = (conversationId: string) =>
    attachments.filter((a) => a.conversationId === conversationId);
  /** Files on the ticket itself rather than on any one message. */
  const looseFiles = attachments.filter((a) => a.conversationId === null);

  const stageFiles = (files: File[]) =>
    setStaged((cur) => [
      ...cur,
      ...files.map((file) => ({
        key: `${file.name}-${file.size}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        file,
        state: 'waiting' as const,
      })),
    ]);

  const proof = useAiProof(tenant, number);
  const suggestion = useSuggestReply(tenant, number);
  // The suggested reply now in the composer, sent with the reply so the
  // server can say how much of it survived (082). Recording only.
  const [fromSuggestion, setFromSuggestion] = useState<{ id: string; text: string } | null>(null);
  const patch = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.patch(`/api/v1/tenants/${tenant}/tickets/${number}`, body),
    onSuccess: () => {
      invalidate();
      toast.success('Ticket updated');
    },
    onError: (e) => toast.error('Update failed', e instanceof Error ? e.message : undefined),
  });

  /**
   * Moving a ticket into a restricted queue (072) is confirmed first: anyone
   * who is not a member loses sight of it the moment it lands. If that
   * includes the person moving it, they are taken back to the queue rather
   * than left on a page that no longer exists for them.
   */
  const navigate = useNavigate();

  /**
   * Escape goes back (AG-2): to the queue with its filters intact when that
   * is where this was opened from, or to the queue itself when the ticket
   * was opened from a link. Radix closes an open dialog or menu on the same
   * keypress before this sees it, but the overlay is still in the DOM, which
   * is what stops one Escape doing both.
   */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const overlayOpen = Boolean(document.querySelector('[role="dialog"], [role="menu"], [role="listbox"]'));
      if (!ticketPageEscape({
          key: e.key,
          metaKey: e.metaKey,
          ctrlKey: e.ctrlKey,
          altKey: e.altKey,
          shiftKey: e.shiftKey,
          defaultPrevented: e.defaultPrevented,
          target: e.target,
          overlayOpen,
        })) return;
      e.preventDefault();
      const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
      if (idx > 0) navigate(-1);
      else navigate('/queue');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate]);

  const [confidentialMove,setConfidentialMove] = useState<{ key: string; name: string } | null>(null);
  const moveConfidential = useMutation({
    mutationFn: (key: string) => api.patch(`/api/v1/tenants/${tenant}/tickets/${number}`, { queueKey: key }),
    onSuccess: async () => {
      const name = confidentialMove?.name ?? 'the restricted queue';
      setConfidentialMove(null);
      const stillMine = await api.get(`/api/v1/tenants/${tenant}/tickets/${number}`).then(
        () => true,
        () => false,
      );
      if (stillMine) {
        invalidate();
        toast.success(`Moved to ${name}`, 'Only its members can see it now.');
      } else {
        toast.success(`Moved to ${name}`, 'Only its members can see it now, so it has left your view.');
        navigate('/queue');
      }
    },
    onError: (e) => toast.error('Not moved', e instanceof Error ? e.message : undefined),
  });

  if (q.isLoading) return <TicketSkeleton />;
  if (q.error) {
    return (
      <div className="p-6">
        <ErrorState error={q.error} />
      </div>
    );
  }
  if (!q.data) return null;

  const {
    ticket: t,
    requester,
    subjectPerson,
    assignee,
    queue,
    conversation,
    periods,
    audit,
    classification,
    slaPolicy,
    legalTransitions,
    showSla,
  } = q.data;

  const blockedPeriod = periods.find(
    (p) => p.exited_at === null && p.state.startsWith('pending_'),
  );
  const publicMessages = conversation.filter((c) => c.kind !== 'note');

  return (
    <div className="mx-auto max-w-[1480px] p-4 sm:p-6">
      {/* Breadcrumb + identity line */}
      <div className="mb-4 flex items-center gap-1.5 text-xs text-ink-tertiary">
        <Link
          to="/queue"
          className="inline-flex items-center gap-1 rounded-xs hover:text-ink-secondary"
        >
          <ArrowLeft aria-hidden className="h-3.5 w-3.5" />
          Queue
        </Link>
        <ChevronRight aria-hidden className="h-3 w-3" />
        <span className="text-ink-secondary">{queue?.name ?? tenant}</span>
        <ChevronRight aria-hidden className="h-3 w-3" />
        <span className="nums text-ink-secondary">#{t.number}</span>
      </div>

      <header className="mb-5 flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="mb-2 flex flex-wrap items-center gap-1.5">
            <StateBadge state={t.state} full />
            {/* WHICH event and WHEN. "Waiting for an event" on its own sends
                the reader to the audit trail to find out what for. */}
            <EventBadge
              firedAt={t.event_fired_at}
              dueAt={t.event_due_at}
              eventName={q.data.eventName ?? t.event_name}
            />
            {showSla && (
              <SlaBadge
                breached={t.resolution_breached}
                dueAt={t.resolution_due_at}
                paused={t.state.startsWith('pending_')}
                state={t.state}
              />
            )}
            <ConfidenceBadge
              confidence={t.route_confidence}
              needsHuman={t.needs_human_routing}
            />
            {t.matched_template && <Badge tone="info">{t.matched_template}</Badge>}
            {/* Beside the other things you read before the subject line. */}
            <PriorityLabel priority={t.priority} />
          </div>

          <h1 className="text-xl font-semibold leading-snug text-ink">{t.subject}</h1>
          <TicketTags
            tenant={tenant}
            number={Number(number)}
            tags={t.tags ?? []}
            canEdit={hasCapability(me.data, tenant, 'ticket.field.write')}
          />

          <p className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-ink-secondary">
            <span className="inline-flex items-center gap-1.5">
              <Avatar name={requester?.name} email={requester?.email} size="xs" />
              {personLabel(requester?.name, requester?.email)}
              {requester?.vip && <VipBadge title={requester.job_title} />}
            </span>
            <span aria-hidden className="text-ink-tertiary">
              ·
            </span>
            <span>{SOURCE_LABEL[t.intake_source] ?? t.intake_source}</span>
            <span aria-hidden className="text-ink-tertiary">
              ·
            </span>
            <span>{dateTime(t.created_at)}</span>
            {subjectPerson && (
              <>
                <span aria-hidden className="text-ink-tertiary">
                  ·
                </span>
                <Badge tone="info" icon={<UserRound />}>
                  about {personLabel(subjectPerson.name, subjectPerson.email)}
                </Badge>
              </>
            )}
            {/* Beside the ticket's own facts rather than in a corner: the
                point is that somebody about to type a reply sees it without
                looking for it. Renders nothing when nobody else is here,
                which is almost every ticket. */}
            {viewers.length > 0 && (
              <span aria-hidden className="text-ink-tertiary">
                ·
              </span>
            )}
            <ViewingNow
              tenant={tenant}
              number={Number(number)}
              viewers={viewers}
              onViewers={setViewers}
            />
          </p>
        </div>

        <div className="flex items-start gap-1">
          <StateControls
            tenant={tenant}
            number={number}
            legal={legalTransitions}
            onDone={invalidate}
            fields={cfg.data?.fields ?? []}
            custom={(t.custom ?? {}) as Record<string, unknown>}
            requestType={t.request_type ?? null}
            requestTypes={cfg.data?.requestTypes ?? []}
            eventTypes={cfg.data?.eventTypes ?? []}
          />
          {/* Merge, transfer, delete — each only with its capability. */}
          <TicketManage tenant={tenant} number={Number(number)} me={me.data} />
        </div>
      </header>

      {blockedPeriod && (
        <Callout tone="accent" className="mb-4" title="Blocked — the resolution clock is paused">
          Waiting since {dateTime(blockedPeriod.entered_at)}
          {blockedPeriod.blocked_party && (
            <>
              {' '}
              on <strong>{blockedPeriod.blocked_party}</strong>
            </>
          )}
          {blockedPeriod.reason && <> — {blockedPeriod.reason}</>}. This ticket is not slow to do;
          it is waiting.
        </Callout>
      )}

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_20.5rem]">
        {/* ---------------- Main column ---------------- */}
        <div className="min-w-0">
          <div className="mb-4">
            <TicketSummary tenant={tenant} number={Number(number)} />
          </div>

          {/* One line above the thread when there is a known answer, and
              nothing at all when there is not. An empty tab reporting that
              a collection could not be searched is the reader's problem
              only if they can act on it, and on a ticket they cannot. */}
          {solutionCount > 0 && (
            <div className="mb-3">
              <KnownSolutionsHint
                tenant={tenant}
                number={number}
                onOpen={() => setTab('solutions')}
              />
            </div>
          )}

          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="mb-4">
              <TabsTrigger value="conversation" count={publicMessages.length}>
                Conversation
              </TabsTrigger>
              {solutionCount > 0 && (
                <TabsTrigger value="solutions" count={solutionCount}>
                  <KnownSolutionsTabIcon live={tab !== 'solutions'} />
                  Known solutions
                </TabsTrigger>
              )}
              {canAct && (
                <TabsTrigger value="actions" count={systemActions.data?.actions.length}>
                  Actions
                </TabsTrigger>
              )}
              <TabsTrigger value="timeline" count={periods.length}>
                Timeline
              </TabsTrigger>
              {audit.length > 0 && (
                <TabsTrigger value="audit" count={audit.length}>
                  Audit
                </TabsTrigger>
              )}
            </TabsList>

            {solutionCount > 0 && (
              <TabsContent value="solutions" className="focus-visible:outline-none">
                <KnownSolutions
                  tenant={tenant}
                  number={number}
                  onUse={(text) => {
                    setReplyKind('reply');
                    setTab('conversation');
                    // Appended, never replaced: an agent who has started
                    // typing must not lose it to a button they pressed to
                    // help.
                    setReply((prev) =>
                      prev.trim() ? `${prev.trimEnd()}

${text}` : text,
                    );
                  }}
                />
              </TabsContent>
            )}

            <TabsContent value="conversation" className="space-y-3 focus-visible:outline-none">
              {/*
                FILES ON THE TICKET RATHER THAN ON A MESSAGE.

                This was computed and never rendered, which made every
                attachment the intake bot uploaded invisible — the bot files a
                ticket and then posts each file with no conversationId,
                exactly as the handoff note tells it to, so the attachments on
                an emailed request are all ticket-level. They were stored,
                returned by the API, and shown nowhere.

                Above the thread, because for the common case they ARE the
                original request's attachments and that is where somebody
                looks for them.
              */}
              {looseFiles.length > 0 && (
                <Panel
                  title={looseFiles.length === 1 ? 'A file on this request' : 'Files on this request'}
                  subtitle="Attached to the request itself rather than to a reply"
                >
                  <AttachmentList attachments={looseFiles} tenant={tenant} compact />
                </Panel>
              )}
              <ol className="space-y-2.5">
                {conversation.map((c) => {
                  const isNote = c.kind === 'note';
                  const inbound = c.kind === 'inbound';
                  return (
                    <li
                      key={c.id}
                      className={cn(
                        'overflow-hidden rounded-lg border shadow-xs',
                        isNote
                          ? 'border-warn-line/50 bg-warn-tint'
                          : inbound
                            ? 'border-line bg-surface-sunken/60'
                            : 'border-line bg-surface',
                      )}
                    >
                      <div className="flex items-center gap-2 border-b border-line-subtle px-3 py-2">
                        <Avatar
                          name={c.author_name}
                          email={c.author_email ?? c.author_label}
                          size="sm"
                        />
                        <span className="truncate text-xs font-medium text-ink">
                          {personLabel(c.author_name, c.author_email ?? c.author_label)}
                        </span>
                        {isNote && (
                          <Badge tone="warn" icon={<Lock />}>
                            private note
                          </Badge>
                        )}
                        {inbound && (
                          <Badge tone="neutral">{SOURCE_LABEL[c.source] ?? c.source}</Badge>
                        )}
                        <span className="ml-auto shrink-0 text-2xs text-ink-tertiary">
                          {dateTime(c.created_at)}
                        </span>
                      </div>
                      {/* Inbound content is untrusted: rendered as text, never HTML.
                          What the desk wrote is the composer's Markdown. */}
                      {inbound ? (
                        <p className="whitespace-pre-wrap px-3 py-2.5 text-sm leading-relaxed text-ink">
                          {c.body}
                        </p>
                      ) : (
                        <MarkdownBody text={c.body} className="px-3 py-2.5" />
                      )}
                      {/* Under the message they arrived with. The server has
                          already filtered a note's files out for a requester,
                          so this renders whatever it was given. */}
                      {filesFor(c.id).length > 0 && (
                        <div className="px-3 pb-2.5">
                          <AttachmentList attachments={filesFor(c.id)} tenant={tenant} compact />
                        </div>
                      )}
                    </li>
                  );
                })}
              </ol>

              {/* Composer */}
              <Panel
                flush
                className={cn(
                  'transition-shadow',
                  replyKind === 'note' && 'border-warn-line/50',
                )}
              >
                <div className="flex items-center justify-between gap-3 border-b border-line-subtle px-3 py-2">
                  <Segmented
                    aria-label="Message type"
                    size="sm"
                    className="w-[16rem]"
                    value={replyKind}
                    onChange={(v) => setReplyKind(v)}
                    options={[
                      { value: 'reply', label: 'Reply to requester' },
                      { value: 'note', label: 'Private note' },
                    ]}
                  />
                  <span className="hidden text-2xs text-ink-tertiary sm:block">
                    {replyKind === 'reply' ? 'Emailed to the requester' : 'Never shown to the requester'}
                  </span>
                </div>

                <AttachControl onFiles={stageFiles} disabled={post.isPending}>
                <div className="p-3">
                  <RichTextEditor
                    ariaLabel={replyKind === 'reply' ? 'Reply' : 'Private note'}
                    placeholder={
                      replyKind === 'reply'
                        ? 'Write a reply. It is emailed to the requester with the ticket reference.'
                        : 'Internal context for whoever picks this up next.'
                    }
                    value={reply}
                    onChange={setReply}
                    disabled={post.isPending}
                    onSubmit={() => {
                      if (reply.trim() || staged.length) post.mutate({ kind: replyKind, body: reply });
                    }}
                    className={replyKind === 'note' ? 'bg-warn-tint/40' : undefined}
                  />
                  <StagedFiles
                    staged={staged}
                    onRemove={(key) => setStaged((cur) => cur.filter((x) => x.key !== key))}
                  />
                  {suggestion.result && replyKind === 'reply' && (
                    <SuggestReplyResult
                      result={suggestion.result}
                      hasDraft={reply.trim().length > 0}
                      onUse={(md) => {
                        setReply(md);
                        if (suggestion.result?.suggestionId) {
                          setFromSuggestion({ id: suggestion.result.suggestionId, text: md });
                        }
                        suggestion.mark('used');
                        suggestion.clear();
                      }}
                      onDismiss={() => {
                        suggestion.mark('dismissed');
                        suggestion.clear();
                      }}
                    />
                  )}
                  {proof.result && (
                    <AiProofResult
                      result={proof.result}
                      current={reply}
                      onUse={(text) => {
                        setReply(text);
                        proof.clear();
                      }}
                      onKeep={proof.clear}
                    />
                  )}
                  <div className="mt-2.5 flex items-center justify-between gap-3">
                    <p className="text-2xs text-ink-tertiary">
                      {staged.length > 0
                        ? `${staged.length} file${staged.length === 1 ? '' : 's'} attached`
                        : reply.trim().length > 0
                          ? `${reply.trim().length} characters`
                          : ' '}
                    </p>
                    <div className="flex items-center gap-1.5">
                    {replyKind === 'reply' && suggestOn && (
                      <SuggestReplyButton pending={suggestion.run.isPending} onClick={() => suggestion.run.mutate()} />
                    )}
                    <AiProofButton
                      pending={proof.run.isPending}
                      disabled={reply.trim().length === 0}
                      onClick={() => proof.run.mutate({ text: reply, kind: replyKind })}
                    />
                    <div className="flex items-center">
                    <Button
                      variant="primary"
                      leading={<Send className="h-3.5 w-3.5" />}
                      loading={post.isPending}
                      /* A file with no words is a legitimate message — "here
                         is the screenshot you asked for" needs no sentence. */
                      disabled={reply.trim().length === 0 && staged.length === 0}
                      onClick={() => post.mutate({ kind: replyKind, body: reply })}
                      className={canWaitOnRequester ? 'rounded-r-none' : undefined}
                    >
                      {replyKind === 'reply' ? 'Send reply' : 'Add note'}
                    </Button>
                    {canWaitOnRequester && (
                      <Menu>
                        <MenuTrigger asChild>
                          <Button
                            variant="primary"
                            iconOnly
                            aria-label="More ways to send"
                            disabled={post.isPending || (reply.trim().length === 0 && staged.length === 0)}
                            className="rounded-l-none border-l border-white/25"
                          >
                            <ChevronDown className="h-3.5 w-3.5" />
                          </Button>
                        </MenuTrigger>
                        <MenuContent>
                          <MenuItem
                            icon={<Hourglass className="h-3.5 w-3.5" />}
                            onSelect={() => post.mutate({ kind: 'reply', body: reply, then: 'pending_requester' })}
                          >
                            Send and wait for the requester
                          </MenuItem>
                        </MenuContent>
                      </Menu>
                    )}
                    </div>
                    </div>
                  </div>
                </div>
                </AttachControl>
              </Panel>
            </TabsContent>

            {canAct && (
              <TabsContent value="actions" className="focus-visible:outline-none">
                <SystemActionsPanel tenant={tenant} number={number} data={systemActions.data} />
              </TabsContent>
            )}

            <TabsContent value="timeline" className="focus-visible:outline-none">
              <Panel
                title="Every state, and how long it lasted"
                subtitle="Business hours, so overnight and weekends do not count against the desk"
              >
                <ol className="relative space-y-3 pl-5">
                  {/* A single continuous rail behind the markers. */}
                  <span
                    aria-hidden
                    className="absolute left-[5px] top-1.5 h-[calc(100%-0.75rem)] w-px bg-line"
                  />
                  {periods.map((p, i) => (
                    <li key={i} className="relative">
                      <span
                        aria-hidden
                        className={cn(
                          'absolute -left-5 top-1 h-[11px] w-[11px] rounded-full border-2 border-surface',
                          p.exited_at === null ? 'bg-brand' : 'bg-line-strong',
                        )}
                      />
                      <div className="flex flex-wrap items-center gap-2">
                        <StateBadge state={p.state} full />
                        <span className="nums text-xs font-medium text-ink">
                          {p.business_minutes === null ? 'ongoing' : minutes(p.business_minutes)}
                        </span>
                        <span className="text-2xs text-ink-tertiary">
                          {dateTime(p.entered_at)}
                        </span>
                      </div>
                      {(p.reason || p.blocked_party) && (
                        <p className="mt-1 text-xs text-ink-secondary">
                          {p.reason}
                          {p.blocked_party && (
                            <>
                              {p.reason ? ' · ' : ''}waiting on{' '}
                              <strong className="text-ink">{p.blocked_party}</strong>
                            </>
                          )}
                        </p>
                      )}
                    </li>
                  ))}
                </ol>
              </Panel>
            </TabsContent>

            {audit.length > 0 && (
              <TabsContent value="audit" className="focus-visible:outline-none">
                <Panel
                  title="Audit trail"
                  subtitle="Append-only — the database refuses updates and deletes on this table"
                  flush
                >
                  <ul className="divide-y divide-line-subtle">
                    {audit.slice(0, 60).map((a, i) => (
                      <li key={i} className="flex gap-3 px-4 py-2 text-2xs">
                        <span className="nums w-28 shrink-0 text-ink-tertiary">
                          {dateTime(a.at)}
                        </span>
                        <span className="w-32 shrink-0 truncate text-ink-secondary">
                          {a.actor_label ?? 'system'}
                        </span>
                        <span className="min-w-0 text-ink">
                          <strong className="font-medium">{a.field}</strong>
                          {a.from_value && (
                            <>
                              {' '}
                              <span className="text-ink-tertiary">{a.from_value}</span> →
                            </>
                          )}{' '}
                          {a.to_value}
                          {a.note && <span className="text-ink-tertiary"> ({a.note})</span>}
                        </span>
                      </li>
                    ))}
                  </ul>
                </Panel>
              </TabsContent>
            )}
          </Tabs>
        </div>

        {/* ---------------- Sidebar ---------------- */}
        <aside className="space-y-4">
          {/* ABOVE Details, deliberately. Whether this ticket is one piece of
              something larger changes how the whole screen should be read, so
              it cannot sit below the queue and the request type. */}
          <TicketLinks tenant={tenant} number={number} links={q.data.links ?? []} />
          <Panel title="Details">
            <dl className="divide-y divide-line-subtle">
              {/* Both settable here. They were read-only, so a ticket that
                  intake could not route sat in Triage with no way out on
                  screen -- the API took a queue, nothing called it. */}
              <Row label="Queue">
                {hasCapability(me.data, tenant, 'ticket.queue.move') && (cfg.data?.queues.length ?? 0) > 0 ? (
                  <Select
                    size="sm"
                    aria-label="Queue"
                    value={queue?.key ?? ''}
                    onValueChange={(v) => {
                      if (v === queue?.key) return;
                      const target = cfg.data?.queues.find((x) => x.key === v);
                      if (target?.restricted) setConfidentialMove({ key: target.key, name: target.name });
                      else patch.mutate({ queueKey: v });
                    }}
                    options={(cfg.data?.queues ?? []).map((x) => ({
                      value: x.key,
                      label: x.restricted ? `${x.name} (restricted)` : x.name,
                    }))}
                  />
                ) : (
                  (queue?.name ?? '—')
                )}
              </Row>
              {cfg.data?.queues.find((x) => x.key === queue?.key)?.restricted && (
                <p className="py-1.5 text-2xs text-warn">
                  Confidential: only members of {queue?.name} and the requester can see this ticket.
                </p>
              )}
              {confidentialMove && (
                <div className="py-2">
                  <Callout tone="warn">
                    <div className="space-y-2">
                      <p>
                        Move to <strong>{confidentialMove.name}</strong>? Only its members will be able to see this
                        ticket. If you are not a member, you will lose sight of it straight away.
                      </p>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => moveConfidential.mutate(confidentialMove.key)}
                          loading={moveConfidential.isPending}
                        >
                          Move it
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setConfidentialMove(null)}>
                          Cancel
                        </Button>
                      </div>
                    </div>
                  </Callout>
                </div>
              )}
              <Row label="Request type">
                {hasCapability(me.data, tenant, 'ticket.field.write') && (cfg.data?.requestTypes.length ?? 0) > 0 ? (
                  <Select
                    size="sm"
                    aria-label="Request type"
                    value={t.request_type ?? NO_TYPE}
                    onValueChange={(v) =>
                      (v === NO_TYPE ? null : v) !== (t.request_type ?? null) &&
                      patch.mutate({ requestType: v === NO_TYPE ? null : v })
                    }
                    options={requestTypeOptions(cfg.data?.requestTypes ?? [], t.request_type ?? null)}
                  />
                ) : (
                  (t.request_type ?? '—')
                )}
              </Row>
              <Row label="Priority">
                {/* Settable here AND from the queue's right-click menu.
                    Somebody usually decides this while scanning a list, not
                    while reading one ticket — but the ticket is where they
                    check it. */}
                <Select
                  size="sm"
                  aria-label="Priority"
                  value={t.priority}
                  onValueChange={(v) => patch.mutate({ priority: v as TicketPriority })}
                  options={PRIORITY_OPTIONS.map((o) => ({
                    value: o.value,
                    label: o.value === 'critical' ? 'Critical — pins to top' : o.label,
                  }))}
                />
              </Row>
              <Row label="Assignee">
                <Select
                  size="sm"
                  aria-label="Assignee"
                  value={t.assignee_oid ?? ''}
                  onValueChange={(v) => patch.mutate({ assigneeOid: v || null })}
                  options={ownerOptions(
                    { oid: t.assignee_oid, name: assignee?.name, email: assignee?.email },
                    cfg.data?.agents,
                  )}
                />
                {assignee?.away && (
                  <span className="mt-1 flex items-center gap-1 text-2xs text-warn">
                    <Plane aria-hidden className="h-3 w-3" />
                    {awayLabel(assignee.awayUntil)}
                  </span>
                )}
              </Row>
              <Row label="Requester">
                <span className="flex items-center gap-1.5">
                  <Avatar name={requester?.name} email={requester?.email} size="xs" />
                  <span className="truncate">
                    {personLabel(requester?.name, requester?.email)}
                  </span>
                </span>
                {requester?.office && (
                  <span className="mt-0.5 block text-2xs text-ink-tertiary">
                    {requester.office}
                  </span>
                )}
              </Row>
              {hasCapability(me.data, tenant, 'ticket.read.queue') && (
                <Row label="Cc">
                  <TicketCc tenant={tenant} number={Number(number)} canEdit={hasCapability(me.data, tenant, 'ticket.field.write')} />
                </Row>
              )}
              {subjectPerson && (
                <Row label="About">{personLabel(subjectPerson.name, subjectPerson.email)}</Row>
              )}
              {t.project_numbers.length > 0 && (
                <Row label="Project">
                  <span className="nums inline-flex items-center gap-1 font-mono text-xs">
                    <Hash aria-hidden className="h-3 w-3 text-ink-tertiary" />
                    {t.project_numbers.join(', ')}
                  </span>
                </Row>
              )}
              {t.systems.length > 0 && (
                <Row label="Systems">
                  <span className="flex flex-wrap gap-1">
                    {t.systems.map((s) => (
                      <Badge key={s} tone="neutral">
                        {s}
                      </Badge>
                    ))}
                  </span>
                </Row>
              )}
              <Row label="Worked">
                <span className="nums">{minutes(t.running_minutes)}</span>
              </Row>
              <Row label="Blocked">
                <span className={cn('nums', t.paused_minutes > 0 && 'text-accent')}>
                  {t.paused_minutes > 0 ? minutes(t.paused_minutes) : '—'}
                </span>
              </Row>
              {showSla && (
                <Row label="Target">
                  <Tooltip content="Consumed in business hours, and paused whenever the ticket is waiting on somebody else.">
                    <span className="nums inline-flex items-center gap-1">
                      <Timer aria-hidden className="h-3 w-3 text-ink-tertiary" />
                      {hours(slaPolicy.resolutionMins / 60)}
                    </span>
                  </Tooltip>
                </Row>
              )}
            </dl>
          </Panel>

          {Object.keys(t.custom).length > 0 && (
            <Panel title="Fields">
              <dl className="divide-y divide-line-subtle">
                {Object.entries(t.custom).map(([k, v]) => {
                  const def = cfg.data?.fields.find((f) => f.key === k);
                  return (
                    <Row key={k} label={def?.label ?? k}>
                      {v === null || v === undefined || v === ''
                        ? '—'
                        : typeof v === 'boolean'
                          ? v
                            ? 'Yes'
                            : 'No'
                          : String(v)}
                    </Row>
                  );
                })}
              </dl>
            </Panel>
          )}

          {classification.length > 0 && (
            <Panel
              title="How this was classified"
              subtitle="Recorded so the classifier can be measured"
            >
              {classification.map((c, i) => (
                <div key={i} className="space-y-2 text-xs">
                  <p className="flex flex-wrap items-center gap-1.5">
                    <Badge tone="brand" icon={<Route />}>
                      {c.rung === null ? `from ${c.source}` : `rung ${c.rung}`}
                    </Badge>
                    {c.matched_rule && (
                      <>
                        <span className="text-ink-secondary">matched</span>
                        <code className="rounded-xs bg-surface-sunken px-1 py-px font-mono text-2xs text-ink">
                          {c.matched_rule}
                        </code>
                      </>
                    )}
                  </p>
                  <p className="text-ink-secondary">
                    Proposed <strong className="text-ink">{c.proposed_request_type ?? 'no type'}</strong>
                    {c.type_by === 'subject' ? (
                      <>
                        , read by the model
                        {c.confidence && <> at {Math.round(Number(c.confidence) * 100)}%</>}
                      </>
                    ) : c.type_by === 'chosen' ? (
                      <>, chosen when it was filed</>
                    ) : (
                      c.confidence && <> at {Math.round(Number(c.confidence) * 100)}% measured purity</>
                    )}
                  </p>
                  {c.human_request_type && (
                    <Callout tone="warn">
                      An agent changed it to <strong>{c.human_request_type}</strong> — recorded as
                      the training signal.
                    </Callout>
                  )}
                </div>
              ))}
            </Panel>
          )}
          {/* BELOW Details and Fields: context, not controls. Both start folded
              to a one-line summary, so they never push the ticket's own
              settings off the screen. */}
          <TicketAsks tenant={tenant} number={Number(number)} me={me.data} />
          <TicketAccessLog
            tenant={tenant}
            number={Number(number)}
            enabled={Boolean(cfg.data?.queues.find((x) => x.key === queue?.key)?.restricted) && hasCapability(me.data, tenant, 'ticket.read.queue')}
          />
          <RequesterHistory tenant={tenant} number={Number(number)} me={me.data} />
          <SolvedBefore tenant={tenant} number={Number(number)} me={me.data} />
        </aside>
      </div>
    </div>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[6.5rem_minmax(0,1fr)] items-baseline gap-3 py-2 first:pt-0 last:pb-0">
      <dt className="text-2xs uppercase tracking-wide text-ink-tertiary">{label}</dt>
      <dd className="min-w-0 break-words text-sm text-ink">{children}</dd>
    </div>
  );
}

/**
 * State controls.
 *
 * The form asks for a reason before it will submit, because the API refuses a
 * pending transition without one — and pending-external without the party.
 * Failing at the server and surfacing an error would be a worse version of
 * the same rule.
 */
function StateControls({
  tenant,
  number,
  legal,
  onDone,
  fields,
  custom,
  requestType,
  requestTypes = [],
  eventTypes,
}: {
  tenant: string;
  number: string;
  legal: TicketState[];
  onDone: () => void;
  /** The group's field definitions, for what it requires at close. */
  fields: FieldDef[];
  /** What this ticket already carries, so an answered field is not re-asked. */
  custom: Record<string, unknown>;
  requestType: string | null;
  /** The desk's types, so a field scoped by key matches a ticket typed by label. */
  requestTypes?: { key: string; label: string }[];
  /** What this desk can park a ticket on. Empty = the option asks for nothing. */
  eventTypes: { key: string; name: string; description: string | null; defaultDays: number | null }[];
}) {
  const toast = useToast();
  const [target, setTarget] = useState<TicketState | ''>('');
  const [reason, setReason] = useState('');
  const [party, setParty] = useState('');
  /** FR-KB-10..12. Captured at resolve, because that is the only moment
   *  somebody both knows the answer and is still looking at the ticket. */
  const [fix, setFix] = useState('');
  const [cause, setCause] = useState('');
  const [selfSolvable, setSelfSolvable] = useState<boolean | null>(null);
  const [closeValues, setCloseValues] = useState<Record<string, string>>({});
  /** Tracks whether the text came from the model, and whether it was edited
   *  — the accept-versus-edit rate is the only measure of the drafting. */
  const [suggested, setSuggested] = useState<string | null>(null);
  const [suggestState, setSuggestState] = useState<'idle' | 'loading' | 'none'>('idle');
  const [eventKey, setEventKey] = useState('');
  const [eventDate, setEventDate] = useState('');

  const mut = useMutation({
    mutationFn: () =>
      api.post(`/api/v1/tenants/${tenant}/tickets/${number}/state`, {
        state: target,
        reason: reason || undefined,
        blockedParty: party || undefined,
        ...(target === 'pending_event'
          ? {
              eventTypeKey: eventKey || undefined,
              eventDueAt: eventDate ? endOfLocalDay(eventDate).toISOString() : undefined,
            }
          : {}),
        ...(target === 'resolved'
          ? {
              fix,
              cause: cause || undefined,
              selfSolvable: selfSolvable ?? undefined,
              ...(outstanding.length
                ? {
                    custom: Object.fromEntries(
                      outstanding.map((f) => [f.key, (closeValues[f.key] ?? '').trim()]),
                    ),
                  }
                : {}),
              fixSource:
                suggested === null ? 'agent' : suggested === fix ? 'accepted_draft' : 'edited_draft',
            }
          : {}),
      }),
    onSuccess: () => {
      toast.success(`Moved to ${target ? STATE_LABEL[target] : 'new state'}`);
      setTarget('');
      setReason('');
      setParty('');
      setFix('');
      setCause('');
      setSelfSolvable(null);
      setCloseValues({});
      setSuggested(null);
      setSuggestState('idle');
      setEventKey('');
      setEventDate('');
      onDone();
    },
    onError: (e) => toast.error('Transition rejected', e instanceof Error ? e.message : undefined),
  });

  const needsReason = target !== '' && PENDING.includes(target);
  const needsParty = target === 'pending_external';
  const parking = target === 'pending_event';
  const resolving = target === 'resolved';

  /**
   * What this close is still missing — the same three conditions the server
   * applies, so the form cannot demand what would not be validated, nor omit
   * what would be.
   */
  const outstanding = !resolving
    ? []
    : fields.filter((f) => {
        if (f.requiredAt !== 'close' || f.visibility === 'requester') return false;
        const scoped = f.requestTypeKeys;
        if (
          scoped &&
          scoped.length > 0 &&
          !scoped.some((k) => sameRequestType(k, requestType ?? null, requestTypes))
        ) {
          return false;
        }
        const v = custom[f.key];
        return v === undefined || v === null || v === '';
      });

  const missingClose = outstanding.some((f) => !(closeValues[f.key] ?? '').trim());

  /** Ask the model what the thread says was done. It proposes; it never records. */
  const suggest = async () => {
    setSuggestState('loading');
    try {
      const r = await api.post<{ suggestion: string | null; reason: string | null }>(
        `/api/v1/tenants/${tenant}/tickets/${number}/resolution/suggest`,
        {},
      );
      if (r.suggestion) {
        setFix(r.suggestion);
        setSuggested(r.suggestion);
        setSuggestState('idle');
      } else {
        setSuggestState('none');
      }
    } catch {
      setSuggestState('none');
    }
  };

  if (legal.length === 0) {
    return (
      <Badge tone="neutral" size="md">
        Terminal — no further transitions
      </Badge>
    );
  }

  return (
    /* `relative`: the expansion below is taken out of flow. See the comment
       on the panel container. */
    <div className="relative w-full max-w-xs shrink-0">
      <div className="flex gap-2">
        <Select
          aria-label="Change state"
          value={target}
          onValueChange={(v) => setTarget(v as TicketState)}
          placeholder="Change state…"
          className="flex-1"
          options={legal.map((s) => ({ value: s, label: STATE_LABEL[s] }))}
        />
        <Button
          variant="primary"
          loading={mut.isPending}
          disabled={
            target === '' ||
            (needsReason && !reason) ||
            (needsParty && !party) ||
            // The server refuses an event wait without both; so does this,
            // rather than letting the button produce the refusal.
            (parking && (!eventKey || !eventDate)) ||
            (resolving && (fix.trim().length < 4 || selfSolvable === null || missingClose))
          }
          onClick={() => mut.mutate()}
        >
          Apply
        </Button>
      </div>

      {/*
        OUT OF FLOW, deliberately.

        These panels used to be ordinary siblings, so the header — a flex row
        with the title on one side and this on the other — grew to whatever
        height the open form needed. The title block is about seventy pixels
        tall and this reaches a hundred and seventy-five, which left a band of
        dead space under the subject line and pushed the blocked banner down
        the page.

        Floating it keeps the header the height of the one control that is
        always there, and this reads as what it is: a form attached to the
        button you just used, open only while you are filling it in.
      */}
      <div className="absolute right-0 top-full z-20 mt-2 w-full space-y-2">
      {resolving && (
        <div className="space-y-2 rounded-md border border-line bg-surface-raised p-2.5 shadow-lg">
          {/* Whatever this group has made mandatory at close, from the same
              definitions the server checks against. */}
          {outstanding.map((f) => (
            <Field key={f.key} label={f.label} required>
              {f.validation.options?.length ? (
                <Select
                  value={closeValues[f.key] ?? ''}
                  onValueChange={(v) => setCloseValues((c) => ({ ...c, [f.key]: v }))}
                  placeholder="Choose…"
                  options={f.validation.options.map((o: string) => ({ value: o, label: o }))}
                />
              ) : (
                <Input
                  value={closeValues[f.key] ?? ''}
                  onChange={(e) => setCloseValues((c) => ({ ...c, [f.key]: e.target.value }))}
                />
              )}
            </Field>
          ))}
          <Field
            label="What fixed it?"
            required
            hint="Not the reply to the requester — the note for whoever hits this next."
          >
            <Textarea
              rows={2}
              value={fix}
              onChange={(e) => setFix(e.target.value)}
              placeholder="Remapped the drive and set it to reconnect at sign-in"
            />
          </Field>

          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              loading={suggestState === 'loading'}
              onClick={() => void suggest()}
            >
              <Sparkles aria-hidden className="h-3.5 w-3.5" />
              Suggest from the thread
            </Button>
            {suggestState === 'none' && (
              <span className="text-2xs text-ink-tertiary">
                The conversation does not say what was done.
              </span>
            )}
            {suggested !== null && suggested === fix && (
              <span className="text-2xs text-ink-tertiary">Drafted — edit if it is wrong.</span>
            )}
          </div>

          <Field
            label="Could the requester have done this themselves?"
            required
            hint="Decides whether a requester-facing article is worth writing. No default."
          >
            <div className="flex gap-1.5">
              <Button
                type="button"
                variant={selfSolvable === true ? 'primary' : 'secondary'}
                size="sm"
                onClick={() => setSelfSolvable(true)}
              >
                Yes
              </Button>
              <Button
                type="button"
                variant={selfSolvable === false ? 'primary' : 'secondary'}
                size="sm"
                onClick={() => setSelfSolvable(false)}
              >
                No — needed the desk
              </Button>
            </div>
          </Field>

          <Field label="What it turned out to be" hint="Optional, when it differs from the report.">
            <Input
              size="sm"
              value={cause}
              onChange={(e) => setCause(e.target.value)}
              placeholder="Group policy had dropped the mapping"
            />
          </Field>
        </div>
      )}

      {needsReason && (
        <div className="space-y-2 rounded-md border border-line bg-surface-raised p-2.5 shadow-lg">
          <Field
            label="Why is it waiting?"
            required
            hint="Required — this is the input to the blocked-time report."
          >
            <Input
              size="sm"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Waiting on a vendor quote"
            />
          </Field>
          {needsParty && (
            <Field label="Who are we waiting on?" required>
              <Input
                size="sm"
                value={party}
                onChange={(e) => setParty(e.target.value)}
                placeholder="Dell, the client, the county…"
              />
            </Field>
          )}
          {parking && (
            <>
              <Field label="What are we waiting for?" required>
                <Select
                  size="sm"
                  aria-label="Event"
                  value={eventKey}
                  placeholder="Choose an event…"
                  options={eventTypes.map((ev) => ({
                    value: ev.key,
                    label: ev.name,
                    hint: ev.description ?? undefined,
                  }))}
                  onValueChange={(key) => {
                    setEventKey(key);
                    const chosen = eventTypes.find((x) => x.key === key);
                    if (chosen?.defaultDays && !eventDate) {
                      const d = new Date();
                      d.setDate(d.getDate() + chosen.defaultDays);
                      setEventDate(dateInputValue(d));
                    }
                  }}
                />
              </Field>
              <Field label="Bring it back on" required>
                <Input
                  size="sm"
                  type="date"
                  value={eventDate}
                  min={dateInputValue(new Date())}
                  onChange={(e) => setEventDate(e.target.value)}
                />
              </Field>
              <p className="text-2xs leading-relaxed text-ink-tertiary">
                The clock stops and it returns to you, flagged, on the day.
                It stays visible under Blocked until then.
              </p>
            </>
          )}
        </div>
      )}
      </div>
    </div>
  );
}

function TicketSkeleton() {
  return (
    <div className="mx-auto max-w-[1480px] p-4 sm:p-6">
      <Skeleton className="h-3 w-40" />
      <Skeleton className="mt-4 h-5 w-24" />
      <Skeleton className="mt-3 h-7 w-2/3" />
      <Skeleton className="mt-2 h-3 w-72" />
      <div className="mt-6 grid gap-4 lg:grid-cols-[minmax(0,1fr)_20.5rem]">
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="rounded-lg border border-line bg-surface p-3">
              <Skeleton className="h-3 w-32" />
              <Skeleton className="mt-3 h-3 w-full" />
              <Skeleton className="mt-1.5 h-3 w-4/5" />
            </div>
          ))}
        </div>
        <div className="rounded-lg border border-line bg-surface p-4">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="flex items-center justify-between gap-3 py-2">
              <Skeleton className="h-2.5 w-16" />
              <Skeleton className="h-2.5 w-24" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

